import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import type { UserRole } from '@/types';
import type { PermissionKey } from '@/hooks/usePermissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  skipBillingCheck?: boolean;
  /** Require the user to have one of these roles. Admins always pass. */
  requiredRole?: UserRole | UserRole[];
  /** Require the user to have this specific permission. Admins always pass. */
  requiredPermission?: PermissionKey;
  /** Where to redirect if role/permission check fails. Defaults to /dashboard. */
  unauthorizedRedirect?: string;
}

function LoadingSpinner({ message }: { message: string }) {
  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent" />
        <p className="mt-3 text-sm text-gray-500">{message}</p>
      </div>
    </div>
  );
}

export default function ProtectedRoute({
  children,
  skipBillingCheck = false,
  requiredRole,
  requiredPermission,
  unauthorizedRedirect = '/dashboard',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasPaidSubscription, checkingBilling, user, token } = useAuth();
  const { can, isAdmin } = usePermissions();

  if (isLoading) {
    return <LoadingSpinner message="Carregando..." />;
  }

  if (token && !user) {
    return <LoadingSpinner message="Autenticando..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!skipBillingCheck && checkingBilling) {
    return <LoadingSpinner message="Verificando assinatura..." />;
  }

  if (!skipBillingCheck && !hasPaidSubscription && user?.companyId !== 1) {
    return <Navigate to="/billing" replace />;
  }

  // Role check (admin always passes)
  if (requiredRole && !isAdmin) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!user || !roles.includes(user.role)) {
      return <Navigate to={unauthorizedRedirect} replace />;
    }
  }

  // Permission check (admin always passes)
  if (requiredPermission && !can(requiredPermission)) {
    return <Navigate to={unauthorizedRedirect} replace />;
  }

  return <>{children}</>;
}

